import { useEffect, useState } from 'react';
import { useAuth, ShellAuthProvider } from '@aimsos/auth';
import { AdminStudioApp } from '@aimsos/admin-studio';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Admin Studio application inside the Core Shell.
 * Mirrors the same embedding pattern as WorkspacesEmbed / DashboardsEmbed.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where Auth0 SDK itself has no session.
 */
const AdminStudioEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/admin-studio/i18n/register')
      .then(({ registerAdminStudioI18n }) => {
        registerAdminStudioI18n(registerNamespace);
        return i18n.loadNamespaces('admin-studio');
      })
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch((err) => console.error('[AdminStudioEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlWS:
        import.meta.env.VITE_API_BASE_URL_WS || import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlAuth: import.meta.env.VITE_API_BASE_URL_AUTH,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !i18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <AdminStudioApp />
    </ShellAuthProvider>
  );
};

export default AdminStudioEmbed;
